import { ActionIcon, Box, Drawer, Group, Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { BrandLogo } from '~portal/components/BrandLogo';
import { drawerSheet } from '~portal/components/chat-history/ChatHistoryDrawer.css';
import { Cmp } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';
import { AccountBlock } from './AccountBlock';
import * as styles from './GlobalNav.css';
import { GlobalNavItems } from './GlobalNavItems';
import { NavSettings } from './NavSettings';

export type GlobalNavDrawerProps = {
  opened: boolean;
  onClose: () => void;
};

/**
 * @desc The global nav below `md`: a left sheet holding the same items, settings row and account
 * block as the desktop column. Mirrors the chat route's `ChatHistoryDrawer` — the same width
 * clamp, close ✕ and safe-area sheet class — so the two drawers read as one pattern.
 */
export const GlobalNavDrawer: Cmp<GlobalNavDrawerProps> = function ({ opened, onClose }) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      // 280px, clamped to the viewport so a narrow phone never loses the close button off-screen.
      size="min(280px, 100vw)"
      position="left"
      padding="sm"
      withOverlay
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.5 }}
      classNames={{ content: drawerSheet }}
      styles={{ body: { height: '100%', display: 'flex', flexDirection: 'column' } }}
    >
      <Group justify="space-between" wrap="nowrap" pb="lg">
        <Link to="/" aria-label="H2GC home" onClick={onClose} className={styles.brandLink}>
          <BrandLogo height={40} />
        </Link>
        <ActionIcon
          onClick={onClose}
          variant="subtle"
          size="lg"
          className={touchTarget}
          aria-label="Close navigation"
        >
          <IconX size={20} />
        </ActionIcon>
      </Group>
      <Box flex={1} className={styles.drawerBody}>
        <GlobalNavItems onNavigate={onClose} />
      </Box>
      <Stack gap="sm" pt="md">
        <NavSettings />
        <AccountBlock />
      </Stack>
    </Drawer>
  );
};
